import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FileUp, LoaderCircle, RotateCcw, Trash2, X } from 'lucide-react'
import { ChangeEvent, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useAdminApi } from '@app/api/api-client-context'
import { mediaKeys, mediaKindLabel, type MediaAsset, type MediaFilePolicy, type MediaKind } from '@entities/media'
import { unwrapApiResponse } from '@shared/api'
import { getInterfaceErrorDescription } from '@shared/i18n/interface-messages'
import { useToast } from '@shared/ui/toast'
import {
	clearUploadAttempt,
	createUploadAttempt,
	isUploadSessionFresh,
	loadUploadAttempt,
	putFile,
	saveUploadAttempt,
	sha256File,
	type UploadAttempt,
	uploadAttemptMatches,
} from '../model/upload'

type UploadPhase = 'idle' | 'hashing' | 'creating' | 'uploading' | 'completing' | 'processing'

const phaseLabel: Record<UploadPhase, string> = {
	idle: 'Готов к загрузке',
	hashing: 'Вычисление SHA-256',
	creating: 'Создание сеанса загрузки',
	uploading: 'Прямая загрузка в object storage',
	completing: 'Проверка контрольной суммы, размера и версии',
	processing: 'Обработка медиафайла',
}

const extension = (filename: string) => {
	const index = filename.lastIndexOf('.')
	return index >= 0 ? filename.slice(index).toLowerCase() : ''
}

const validateFile = (file: File, kind: MediaKind, policy: MediaFilePolicy) => {
	const kindPolicy = policy.kinds.find(item => item.kind === kind)
	if (!kindPolicy) throw new Error(`Политика для ${mediaKindLabel[kind]} не найдена`)
	if (file.size <= 0) throw new Error('Пустой файл нельзя загрузить')
	if (file.size > kindPolicy.max_size_bytes) throw new Error(`Файл превышает лимит ${formatBytes(kindPolicy.max_size_bytes)}`)
	if (!kindPolicy.content_types.includes(file.type)) throw new Error(`Content-Type ${file.type || 'не определён'} не разрешён`)
	if (!kindPolicy.extensions.includes(extension(file.name))) throw new Error(`Расширение ${extension(file.name) || 'не определено'} не разрешено`)
}

const formatBytes = (value: number) => {
	if (value < 1024) return `${value} Б`
	if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} КБ`
	return `${(value / 1024 ** 2).toFixed(1)} МБ`
}

const wait = (milliseconds: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
	const timer = window.setTimeout(resolve, milliseconds)
	const abort = () => {
		window.clearTimeout(timer)
		reject(new DOMException('Операция отменена', 'AbortError'))
	}
	signal.addEventListener('abort', abort, { once: true })
})

export const MediaUploadDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
	const api = useAdminApi()
	const queryClient = useQueryClient()
	const { notify } = useToast()
	const restoredAttempt = useMemo(() => loadUploadAttempt(), [])
	const [kind, setKind] = useState<MediaKind>(restoredAttempt?.kind ?? 'IMAGE')
	const [file, setFile] = useState<File | null>(null)
	const [attempt, setAttempt] = useState<UploadAttempt | null>(restoredAttempt)
	const [phase, setPhase] = useState<UploadPhase>('idle')
	const [progress, setProgress] = useState(0)
	const [error, setError] = useState<string | null>(null)
	const abortRef = useRef<AbortController | null>(null)
	const policyQuery = useQuery({
		queryKey: mediaKeys.policy,
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/media/policy')).data,
		staleTime: 5 * 60_000,
	})
	const busy = phase !== 'idle'
	const policy = policyQuery.data
	const accept = useMemo(() => policy?.kinds.find(item => item.kind === kind)?.extensions.join(',') ?? '', [kind, policy])
	const resumableWithoutFile = Boolean(attempt?.session && (attempt.uploadEtag || attempt.completed))
	const canStart = Boolean(policy && (file || resumableWithoutFile))

	const persist = (next: UploadAttempt) => {
		saveUploadAttempt(next)
		setAttempt(next)
		return next
	}
	const discard = () => {
		clearUploadAttempt()
		setAttempt(null)
		setFile(null)
		setProgress(0)
		setError(null)
	}
	const close = () => {
		if (busy) return
		setFile(null)
		setProgress(0)
		setError(null)
		onOpenChange(false)
	}
	const changeKind = (nextKind: MediaKind) => {
		if (busy) return
		setKind(nextKind)
		setFile(null)
		setError(null)
	}
	const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
		const selected = event.target.files?.[0] ?? null
		setFile(selected)
		setError(null)
		if (!selected) return
		if (uploadAttemptMatches(attempt, selected, kind)) return
		persist(createUploadAttempt(selected, kind))
	}
	const cancel = () => abortRef.current?.abort()
	const pollAsset = async (assetId: string, controller: AbortController): Promise<MediaAsset | null> => {
		for (let index = 0; index < 80; index += 1) {
			const asset = unwrapApiResponse(await api.GET('/admin/v1/media/{asset_id}', {
				params: { path: { asset_id: assetId } },
			})).data
			await queryClient.setQueryData(mediaKeys.detail(assetId), asset)
			await queryClient.invalidateQueries({ queryKey: mediaKeys.all })
			if (asset.state === 'READY' || asset.state === 'REJECTED') return asset
			await wait(1_500, controller.signal)
		}
		return null
	}
	const upload = async () => {
		if (!policy) return
		const controller = new AbortController()
		abortRef.current = controller
		setError(null)
		try {
			let current = attempt
			if (file) {
				validateFile(file, kind, policy)
				if (!uploadAttemptMatches(current, file, kind)) current = persist(createUploadAttempt(file, kind))
			}
			if (!current) throw new Error('Выберите файл для загрузки')

			if (!current.checksum) {
				if (!file) throw new Error('Повторно выберите исходный файл для вычисления контрольной суммы')
				setPhase('hashing')
				const checksum = await sha256File(file, {
					signal: controller.signal,
					onProgress: value => setProgress(Math.round(value.percent * 0.2)),
				})
				current = persist({ ...current, checksum })
			}

			if (current.session && !current.uploadEtag && !current.completed && !isUploadSessionFresh(current)) {
				if (!file) throw new Error('Сеанс загрузки истёк. Повторно выберите исходный файл')
				current = persist({ ...createUploadAttempt(file, kind), checksum: current.checksum })
			}

			const checksum = current.checksum
			if (!checksum) throw new Error('Состояние контрольной суммы не сохранено')

			if (!current.session) {
				setPhase('creating')
				setProgress(22)
				const session = unwrapApiResponse(await api.POST('/admin/v1/media/uploads', {
					params: { header: { 'Idempotency-Key': current.createIdempotencyKey } },
					body: {
						kind: current.kind,
						filename: current.filename,
						content_type: current.contentType,
						size_bytes: current.size,
						sha256: checksum,
					},
				})).data
				current = persist({
					...current,
					session: {
						assetId: session.asset_id,
						uploadUrl: session.upload_url,
						expiresAt: session.expires_at,
						headers: session.headers,
					},
				})
			}

			const uploadSession = current.session
			if (!uploadSession) throw new Error('Состояние сеанса загрузки не сохранено')

			if (!current.uploadEtag) {
				if (!file) throw new Error('Повторно выберите исходный файл для продолжения direct upload')
				setPhase('uploading')
				const uploaded = await putFile({
					file,
					url: uploadSession.uploadUrl,
					headers: uploadSession.headers,
					signal: controller.signal,
					onProgress: value => setProgress(22 + Math.round(value.percent * 0.68)),
				})
				current = persist({ ...current, uploadEtag: uploaded.etag })
			}

			const uploadEtag = current.uploadEtag
			if (!uploadEtag) throw new Error('Upload ETag state was not persisted')

			if (!current.completed) {
				setPhase('completing')
				setProgress(94)
				await api.POST('/admin/v1/media/{asset_id}/complete', {
					params: {
						path: { asset_id: uploadSession.assetId },
						header: { 'Idempotency-Key': current.completeIdempotencyKey },
					},
					body: { etag: uploadEtag, size_bytes: current.size },
				}).then(unwrapApiResponse)
				current = persist({ ...current, completed: true })
			}

			setPhase('processing')
			setProgress(97)
			const terminal = await pollAsset(uploadSession.assetId, controller)
			if (!terminal) {
				setError('Обработка продолжается. Попытка сохранена — проверку статуса можно продолжить позже без повторной загрузки файла.')
				setPhase('idle')
				return
			}
			clearUploadAttempt()
			setAttempt(null)
			setProgress(100)
			if (terminal.state === 'REJECTED') {
				setError('Обработка файла отклонена. Откройте диагностику ассета, исправьте файл и загрузите его заново.')
				setPhase('idle')
				return
			}
			await queryClient.invalidateQueries({ queryKey: mediaKeys.all })
			notify({ title: 'Медиафайл готов', description: `${terminal.filename}: создано вариантов ${terminal.variants?.length ?? 0}`, tone: 'success' })
			setFile(null)
			setPhase('idle')
			onOpenChange(false)
		} catch (reason) {
			if (reason instanceof DOMException && reason.name === 'AbortError') {
				setError('Операция остановлена. Состояние попытки сохранено для безопасного продолжения.')
			} else {
				setError(getInterfaceErrorDescription(reason, 'Не удалось загрузить файл'))
			}
			setPhase('idle')
		} finally {
			abortRef.current = null
		}
	}

	return (
		<Dialog open={open} onOpenChange={value => { if (!value) close() }}>
			<DialogContent className='sm:max-w-xl'>
				<DialogHeader>
					<DialogTitle>Загрузить медиафайл</DialogTitle>
					<DialogDescription>Файл передаётся напрямую в объектное хранилище. Сведения о загрузке, контрольная сумма, версия объекта и идентификатор безопасной повторной попытки сохраняются локально до завершения обработки.</DialogDescription>
				</DialogHeader>
				<div className='space-y-5'>
					<label className='block space-y-2 text-sm font-medium'>Тип ассета
						<select value={kind} onChange={event => changeKind(event.target.value as MediaKind)} disabled={busy} className='h-10 w-full rounded-md border bg-background px-3 text-sm'>
							{Object.entries(mediaKindLabel).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
						</select>
					</label>
					<label className='block space-y-2 text-sm font-medium'>Файл
						<Input type='file' accept={accept} onChange={selectFile} disabled={busy || !policy} />
					</label>
					{file ? <div className='rounded-xl border bg-muted/30 p-4 text-sm'><p className='font-medium'>{file.name}</p><p className='mt-1 text-muted-foreground'>{file.type || 'unknown'} · {formatBytes(file.size)}</p>{uploadAttemptMatches(attempt, file, kind) ? <p className='mt-2 text-xs text-emerald-700'>Будет продолжена сохранённая попытка с теми же idempotency keys.</p> : null}</div> : null}
					{attempt && !file ? <div className='rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900'><p className='font-medium'>Незавершённая попытка: {attempt.filename}</p><p className='mt-1'>{formatBytes(attempt.size)} · {mediaKindLabel[attempt.kind]}</p><p className='mt-2 text-xs'>{attempt.completed ? 'Байты уже подтверждены; можно продолжить polling без повторного выбора файла.' : attempt.uploadEtag ? 'Direct upload завершён; можно повторить completion без файла.' : 'Для продолжения выберите тот же исходный файл.'}</p></div> : null}
					{busy ? <div className='space-y-2' aria-live='polite'><div className='flex items-center justify-between text-sm'><span className='inline-flex items-center gap-2'><LoaderCircle className='h-4 w-4 animate-spin' />{phaseLabel[phase]}</span><span>{progress}%</span></div><div className='h-2 overflow-hidden rounded-full bg-muted'><div className='h-full bg-primary transition-[width]' style={{ width: `${progress}%` }} /></div></div> : null}
					{error ? <div role='alert' className='rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800'>{error}</div> : null}
				</div>
				<DialogFooter className='sm:justify-between'>
					<div>{attempt && !busy ? <Button variant='ghost' onClick={discard}><Trash2 className='h-4 w-4' /> Сбросить попытку</Button> : null}</div>
					<div className='flex gap-2'>
						{busy ? <Button variant='outline' onClick={cancel}><X className='h-4 w-4' /> Остановить</Button> : <Button variant='outline' onClick={close}>Закрыть</Button>}
						<Button onClick={() => void upload()} disabled={!canStart || busy}>{attempt ? <RotateCcw className='h-4 w-4' /> : <FileUp className='h-4 w-4' />} {attempt ? 'Продолжить' : 'Загрузить'}</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
